
import * as THREE from 'three';

// Utility function used in presets
function nsin(val: number) {
    return Math.sin(val) * 0.5 + 0.5;
}

const mountainUniforms = {
    uFreq: { value: new THREE.Vector3(3, 6, 10) },
    uAmp: { value: new THREE.Vector3(30, 30, 20) }
};

const xyUniforms = {
    uFreq: { value: new THREE.Vector2(5, 2) },
    uAmp: { value: new THREE.Vector2(25, 15) }
};

const LongRaceUniforms = {
    uFreq: { value: new THREE.Vector2(2, 3) },
    uAmp: { value: new THREE.Vector2(35, 10) }
};

const turbulentUniforms = {
    uFreq: { value: new THREE.Vector4(4, 8, 8, 1) },
    uAmp: { value: new THREE.Vector4(25, 5, 10, 10) }
};

const deepUniforms = {
    uFreq: { value: new THREE.Vector2(4, 8) },
    uAmp: { value: new THREE.Vector2(10, 20) },
    uPowY: { value: new THREE.Vector2(20, 2) }
};

export const hyperspeedPresets = {
    one: {
        onSpeedUp: () => { },
        onSlowDown: () => { },
        distortion: 'turbulentDistortion',
        length: 400,
        roadWidth: 10,
        islandWidth: 2,
        lanesPerRoad: 3,
        fov: 90,
        fovSpeedUp: 150,
        speedUp: 2,
        carLightsFade: 0.4,
        totalSideLightSticks: 20,
        lightPairsPerRoadWay: 40,
        shoulderLinesWidthPercentage: 0.05,
        brokenLinesWidthPercentage: 0.1,
        brokenLinesLengthPercentage: 0.5,
        lightStickWidth: [0.12, 0.5],
        lightStickHeight: [1.3, 1.7],
        movingAwaySpeed: [60, 80],
        movingCloserSpeed: [-120, -160],
        carLightsLength: [400 * 0.03, 400 * 0.2],
        carLightsRadius: [0.05, 0.14],
        carWidthPercentage: [0.3, 0.5],
        carShiftX: [-0.8, 0.8],
        carFloorSeparation: [0, 5],
        colors: {
            roadColor: 0x080808,
            islandColor: 0x0a0a0a,
            background: 0x000000,
            shoulderLines: 0x131318,
            brokenLines: 0x131318,
            leftCars: [0xd856bf, 0x6750a2, 0xc247ac],
            rightCars: [0x03b3c3, 0x0e5ea5, 0x324555],
            sticks: 0x03b3c3
        }
    },
    two: {
        onSpeedUp: () => { },
        onSlowDown: () => { },
        distortion: 'mountainDistortion',
        length: 400,
        roadWidth: 9,
        islandWidth: 2,
        lanesPerRoad: 3,
        fov: 90,
        fovSpeedUp: 150,
        speedUp: 2,
        carLightsFade: 0.4,
        totalSideLightSticks: 50,
        lightPairsPerRoadWay: 50,
        shoulderLinesWidthPercentage: 0.05,
        brokenLinesWidthPercentage: 0.1,
        brokenLinesLengthPercentage: 0.5,
        lightStickWidth: [0.12, 0.5],
        lightStickHeight: [1.3, 1.7],

        movingAwaySpeed: [60, 80],
        movingCloserSpeed: [-120, -160],
        carLightsLength: [400 * 0.05, 400 * 0.15],
        carLightsRadius: [0.05, 0.14],
        carWidthPercentage: [0.3, 0.5],
        carShiftX: [-0.2, 0.2],
        carFloorSeparation: [0.05, 1],
        colors: {
            roadColor: 0x080808,
            islandColor: 0x0a0a0a,
            background: 0x000000,
            shoulderLines: 0x131318,
            brokenLines: 0x131318,
            leftCars: [0xff102a, 0xeb383e, 0xff102a],
            rightCars: [0xdadafa, 0xbebae3, 0x8f97e4],
            sticks: 0xdadafa
        }
    },
    three: {
        onSpeedUp: () => { },
        onSlowDown: () => { },
        distortion: 'xyDistortion',
        length: 400,
        roadWidth: 9,
        islandWidth: 2,
        lanesPerRoad: 3,
        fov: 90,
        fovSpeedUp: 150,
        speedUp: 3,
        carLightsFade: 0.4,
        totalSideLightSticks: 50,
        lightPairsPerRoadWay: 30,
        shoulderLinesWidthPercentage: 0.05,
        brokenLinesWidthPercentage: 0.1,
        brokenLinesLengthPercentage: 0.5,
        lightStickWidth: [0.02, 0.05],
        lightStickHeight: [0.3, 0.7],
        movingAwaySpeed: [20, 50],
        movingCloserSpeed: [-150, -230],
        carLightsLength: [400 * 0.05, 400 * 0.2],
        carLightsRadius: [0.03, 0.08],
        carWidthPercentage: [0.1, 0.5],
        carShiftX: [-0.5, 0.5],
        carFloorSeparation: [0, 0.1],
        colors: {
            roadColor: 0x080808,
            islandColor: 0x0a0a0a,
            background: 0x000000,
            shoulderLines: 0x131318,
            brokenLines: 0x131318,
            leftCars: [0x7d0d1b, 0xa90519, 0xff102a],
            rightCars: [0xf1eece, 0xe6e2b1, 0xdfd98a],
            sticks: 0xf1eece
        }
    },
    four: {
        onSpeedUp: () => { },
        onSlowDown: () => { },
        distortion: 'LongRaceDistortion',
        length: 400,
        roadWidth: 10,
        islandWidth: 5,
        lanesPerRoad: 2,
        fov: 90,
        fovSpeedUp: 150,
        speedUp: 2,
        carLightsFade: 0.4,
        totalSideLightSticks: 50,
        lightPairsPerRoadWay: 70,
        shoulderLinesWidthPercentage: 0.05,
        brokenLinesWidthPercentage: 0.1,
        brokenLinesLengthPercentage: 0.5,
        lightStickWidth: [0.12, 0.5],
        lightStickHeight: [1.3, 1.7],
        movingAwaySpeed: [60, 80],
        movingCloserSpeed: [-120, -160],
        carLightsLength: [400 * 0.05, 400 * 0.15],
        carLightsRadius: [0.05, 0.14],
        carWidthPercentage: [0.3, 0.5],
        carShiftX: [-0.2, 0.2],
        carFloorSeparation: [0.05, 1],
        colors: {
            roadColor: 0x080808,
            islandColor: 0x0a0a0a,
            background: 0x000000,
            shoulderLines: 0x131318,
            brokenLines: 0x131318,
            leftCars: [0xff5f73, 0xe74d60, 0xff102a],
            rightCars: [0xa4e3e6, 0x80d1d4, 0x53c2c6],
            sticks: 0xa4e3e6
        }
    },
    five: {
        onSpeedUp: () => { },
        onSlowDown: () => { },
        distortion: 'turbulentDistortion',
        length: 400,
        roadWidth: 9,
        islandWidth: 2,
        lanesPerRoad: 3,
        fov: 90,
        fovSpeedUp: 150,
        speedUp: 2,
        carLightsFade: 0.4,
        totalSideLightSticks: 50,
        lightPairsPerRoadWay: 50,
        shoulderLinesWidthPercentage: 0.05,
        brokenLinesWidthPercentage: 0.1,
        brokenLinesLengthPercentage: 0.5,
        lightStickWidth: [0.12, 0.5],
        lightStickHeight: [1.3, 1.7],
        movingAwaySpeed: [60, 80],
        movingCloserSpeed: [-120, -160],
        carLightsLength: [400 * 0.05, 400 * 0.15],
        carLightsRadius: [0.05, 0.14],
        carWidthPercentage: [0.3, 0.5],
        carShiftX: [-0.2, 0.2],
        carFloorSeparation: [0.05, 1],
        colors: {
            roadColor: 0x080808,
            islandColor: 0x0a0a0a,
            background: 0x000000,
            shoulderLines: 0x131318,
            brokenLines: 0x131318,
            leftCars: [0xdc5b20, 0xdca320, 0xdc2020],
            rightCars: [0x334bf7, 0xe5e6ed, 0xbfc6f3],
            sticks: 0xc5e8eb
        }
    },
    six: {
        onSpeedUp: () => { },
        onSlowDown: () => { },
        distortion: 'deepDistortion',
        length: 400,
        roadWidth: 18,
        islandWidth: 2,
        lanesPerRoad: 3,
        fov: 90,
        fovSpeedUp: 150,
        speedUp: 2,
        carLightsFade: 0.4,
        totalSideLightSticks: 50,
        lightPairsPerRoadWay: 50,
        shoulderLinesWidthPercentage: 0.05,
        brokenLinesWidthPercentage: 0.1,
        brokenLinesLengthPercentage: 0.5,
        lightStickWidth: [0.12, 0.5],
        lightStickHeight: [1.3, 1.7],
        movingAwaySpeed: [60, 80],
        movingCloserSpeed: [-120, -160],
        carLightsLength: [400 * 0.05, 400 * 0.15],
        carLightsRadius: [0.05, 0.14],
        carWidthPercentage: [0.3, 0.5],
        carShiftX: [-0.2, 0.2],
        carFloorSeparation: [0.05, 1],
        colors: {
            roadColor: 0x080808,
            islandColor: 0x0a0a0a,
            background: 0x000000,
            shoulderLines: 0x131318,
            brokenLines: 0x131318,
            leftCars: [0xff322f, 0xa33010, 0xa81508],
            rightCars: [0xfdfdf0, 0xf3dea0, 0xe2bb88],
            sticks: 0xfdfdf0
        }
    }
};

interface Distortion {
    uniforms: Record<string, { value: any }>;
    getDistortion: string;
    getJS?: (progress: number, time: number) => THREE.Vector3;
}

interface Distortions {
    [key: string]: Distortion;
}

export const distortions: Distortions = {
    mountainDistortion: {
        uniforms: mountainUniforms,
        getDistortion: `
      uniform vec3 uAmp;
      uniform vec3 uFreq;
      #define PI 3.14159265358979
      float nsin(float val){
        return sin(val) * 0.5 + 0.5;
      }
      vec3 getDistortion(float progress){
        float movementProgressFix = 0.02;
        return vec3( 
          cos(progress * PI * uFreq.x + uTime) * uAmp.x - cos(movementProgressFix * PI * uFreq.x + uTime) * uAmp.x,
          nsin(progress * PI * uFreq.y + uTime) * uAmp.y - nsin(movementProgressFix * PI * uFreq.y + uTime) * uAmp.y,
          nsin(progress * PI * uFreq.z + uTime) * uAmp.z - nsin(movementProgressFix * PI * uFreq.z + uTime) * uAmp.z
        );
      }
    `,
        getJS: (progress: number, time: number) => {
            const movementProgressFix = 0.02;
            const uFreq = mountainUniforms.uFreq.value;
            const uAmp = mountainUniforms.uAmp.value;
            const distortion = new THREE.Vector3(
                Math.cos(progress * Math.PI * uFreq.x + time) * uAmp.x -
                Math.cos(movementProgressFix * Math.PI * uFreq.x + time) * uAmp.x,
                nsin(progress * Math.PI * uFreq.y + time) * uAmp.y -
                nsin(movementProgressFix * Math.PI * uFreq.y + time) * uAmp.y,
                nsin(progress * Math.PI * uFreq.z + time) * uAmp.z -
                nsin(movementProgressFix * Math.PI * uFreq.z + time) * uAmp.z
            );
            const lookAtAmp = new THREE.Vector3(2, 2, 2);
            const lookAtOffset = new THREE.Vector3(0, 0, -5);
            return distortion.multiply(lookAtAmp).add(lookAtOffset);
        }
    },
    xyDistortion: {
        uniforms: xyUniforms,
        getDistortion: `
      uniform vec2 uFreq;
      uniform vec2 uAmp;
      #define PI 3.14159265358979
      vec3 getDistortion(float progress){
        float movementProgressFix = 0.02;
        return vec3( 
          cos(progress * PI * uFreq.x + uTime) * uAmp.x - cos(movementProgressFix * PI * uFreq.x + uTime) * uAmp.x,
          sin(progress * PI * uFreq.y + PI/2. + uTime) * uAmp.y - sin(movementProgressFix * PI * uFreq.y + PI/2. + uTime) * uAmp.y,
          0.
        );
      }
    `,
        getJS: (progress: number, time: number) => {
            const movementProgressFix = 0.02;
            const uFreq = xyUniforms.uFreq.value;
            const uAmp = xyUniforms.uAmp.value;
            const distortion = new THREE.Vector3(
                Math.cos(progress * Math.PI * uFreq.x + time) * uAmp.x -
                Math.cos(movementProgressFix * Math.PI * uFreq.x + time) * uAmp.x,
                Math.sin(progress * Math.PI * uFreq.y + time + Math.PI / 2) * uAmp.y -
                Math.sin(movementProgressFix * Math.PI * uFreq.y + time + Math.PI / 2) * uAmp.y,
                0
            );
            const lookAtAmp = new THREE.Vector3(2, 0.4, 1);
            const lookAtOffset = new THREE.Vector3(0, 0, -3);
            return distortion.multiply(lookAtAmp).add(lookAtOffset);
        }
    },
    LongRaceDistortion: {
        uniforms: LongRaceUniforms,
        getDistortion: `
      uniform vec2 uFreq;
      uniform vec2 uAmp;
      #define PI 3.14159265358979
      vec3 getDistortion(float progress){
        float camProgress = 0.0125;
        return vec3( 
          sin(progress * PI * uFreq.x + uTime) * uAmp.x - sin(camProgress * PI * uFreq.x + uTime) * uAmp.x,
          sin(progress * PI * uFreq.y + uTime) * uAmp.y - sin(camProgress * PI * uFreq.y + uTime) * uAmp.y,
          0.
        );
      }
    `,
        getJS: (progress: number, time: number) => {
            const camProgress = 0.0125;
            const uFreq = LongRaceUniforms.uFreq.value;
            const uAmp = LongRaceUniforms.uAmp.value;
            const distortion = new THREE.Vector3(
                Math.sin(progress * Math.PI * uFreq.x + time) * uAmp.x -
                Math.sin(camProgress * Math.PI * uFreq.x + time) * uAmp.x,
                Math.sin(progress * Math.PI * uFreq.y + time) * uAmp.y -
                Math.sin(camProgress * Math.PI * uFreq.y + time) * uAmp.y,
                0
            );
            const lookAtAmp = new THREE.Vector3(1, 1, 0);
            const lookAtOffset = new THREE.Vector3(0, 0, -5);
            return distortion.multiply(lookAtAmp).add(lookAtOffset);
        }
    },
    turbulentDistortion: {
        uniforms: turbulentUniforms,
        getDistortion: `
      uniform vec4 uFreq;
      uniform vec4 uAmp;
      float nsin(float val){
        return sin(val) * 0.5 + 0.5;
      }
      #define PI 3.14159265358979
      float getDistortionX(float progress){
        return (
          cos(PI * progress * uFreq.r + uTime) * uAmp.r +
          pow(cos(PI * progress * uFreq.g + uTime * (uFreq.g / uFreq.r)), 2. ) * uAmp.g
        );
      }
      float getDistortionY(float progress){
        return (
          -nsin(PI * progress * uFreq.b + uTime) * uAmp.b +
          -pow(nsin(PI * progress * uFreq.a + uTime / (uFreq.b / uFreq.a)), 5.) * uAmp.a
        );
      }
      vec3 getDistortion(float progress){
        return vec3(
          getDistortionX(progress) - getDistortionX(0.0125),
          getDistortionY(progress) - getDistortionY(0.0125),
          0.
        );
      }
    `,
        getJS: (progress: number, time: number) => {
            const uFreq = turbulentUniforms.uFreq.value;
            const uAmp = turbulentUniforms.uAmp.value;

            const getX = (p: number) =>
                Math.cos(Math.PI * p * uFreq.x + time) * uAmp.x +
                Math.pow(Math.cos(Math.PI * p * uFreq.y + time * (uFreq.y / uFreq.x)), 2) * uAmp.y;

            const getY = (p: number) =>
                -nsin(Math.PI * p * uFreq.z + time) * uAmp.z -
                Math.pow(nsin(Math.PI * p * uFreq.w + time / (uFreq.z / uFreq.w)), 5) * uAmp.w;

            const distortion = new THREE.Vector3(
                getX(progress) - getX(progress + 0.007),
                getY(progress) - getY(progress + 0.007),
                0
            );
            const lookAtAmp = new THREE.Vector3(-2, -5, 0);
            const lookAtOffset = new THREE.Vector3(0, 0, -10);
            return distortion.multiply(lookAtAmp).add(lookAtOffset);
        }
    },
    turbulentDistortionStill: {
        uniforms: turbulentUniforms,
        getDistortion: `
      uniform vec4 uFreq;
      uniform vec4 uAmp;
      float nsin(float val){
        return sin(val) * 0.5 + 0.5;
      }
      #define PI 3.14159265358979
      float getDistortionX(float progress){
        return (
          cos(PI * progress * uFreq.r) * uAmp.r +
          pow(cos(PI * progress * uFreq.g * (uFreq.g / uFreq.r)), 2. ) * uAmp.g
        );
      }
      float getDistortionY(float progress){
        return (
          -nsin(PI * progress * uFreq.b) * uAmp.b +
          -pow(nsin(PI * progress * uFreq.a / (uFreq.b / uFreq.a)), 5.) * uAmp.a
        );
      }
      vec3 getDistortion(float progress){
        return vec3(
          getDistortionX(progress) - getDistortionX(0.02),
          getDistortionY(progress) - getDistortionY(0.02),
          0.
        );
      }
    `,
        getJS: undefined
    },
    deepDistortionStill: {
        uniforms: deepUniforms,
        getDistortion: `
      uniform vec4 uFreq;
      uniform vec4 uAmp;
      uniform vec2 uPowY;
      float nsin(float val){
        return sin(val) * 0.5 + 0.5;
      }
      #define PI 3.14159265358979
      float getDistortionX(float progress){
        return (
          sin(progress * PI * uFreq.x) * uAmp.x * 2.
        );
      }
      float getDistortionY(float progress){
        return (
          pow(abs(progress * uPowY.x), uPowY.y) + sin(progress * PI * uFreq.y) * uAmp.y
        );
      }
      vec3 getDistortion(float progress){
        return vec3(
          getDistortionX(progress) - getDistortionX(0.02),
          getDistortionY(progress) - getDistortionY(0.05),
          0.
        );
      }
    `,
        getJS: undefined
    },
    deepDistortion: {
        uniforms: deepUniforms,
        getDistortion: `
      uniform vec4 uFreq;
      uniform vec4 uAmp;
      uniform vec2 uPowY;
      float nsin(float val){
        return sin(val) * 0.5 + 0.5;
      }
      #define PI 3.14159265358979
      float getDistortionX(float progress){
        return (
          sin(progress * PI * uFreq.x + uTime) * uAmp.x
        );
      }
      float getDistortionY(float progress){
        return (
          pow(abs(progress * uPowY.x), uPowY.y) + sin(progress * PI * uFreq.y + uTime) * uAmp.y
        );
      }
      vec3 getDistortion(float progress){
        return vec3(
          getDistortionX(progress) - getDistortionX(0.02),
          getDistortionY(progress) - getDistortionY(0.02),
          0.
        );
      }
    `,
        getJS: (progress: number, time: number) => {
            const uFreq = deepUniforms.uFreq.value;
            const uAmp = deepUniforms.uAmp.value;
            const uPowY = deepUniforms.uPowY.value;

            const getX = (p: number) => Math.sin(p * Math.PI * uFreq.x + time) * uAmp.x;
            const getY = (p: number) => Math.pow(p * uPowY.x, uPowY.y) + Math.sin(p * Math.PI * uFreq.y + time) * uAmp.y;

            const distortion = new THREE.Vector3(
                getX(progress) - getX(progress + 0.01),
                getY(progress) - getY(progress + 0.01),
                0
            );
            const lookAtAmp = new THREE.Vector3(-2, -4, 0);
            const lookAtOffset = new THREE.Vector3(0, 0, -10);
            return distortion.multiply(lookAtAmp).add(lookAtOffset);
        }
    }
};
